import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { FormatUtils } from '../../../../../libs/map/utils/format.utils';
import { MapUtils } from '../../../../../libs/map/utils/map.utils';
import { MapSize } from '../../domain/enums/map-size';
import { Address, Street, WeekOfYear } from '../../domain/models';
import { SectorPolygon } from '../../domain/models/sector/sector-polygon';
import Constants from '../../shared/constants';
import { DistributionViewModel } from '../view-models/distribution.view-model';
import { DistributionListItemViewModel } from '../view-models/distribution-list-item.view-model';
import * as AllActions from './distribution.actions';
import { DistributionState } from './distribution.state';
import { DistributionTiming } from '../../domain/enums/distribution-timing';
import * as _ from 'lodash';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import { transform } from 'ol/proj';
import { ProjectionLevel } from '../../domain/enums/projection-level';
import { EventType } from '../../domain/enums/event-type';
import { DeviceTrackingData } from '../../domain/models/tracking/device_tracking_data';
import { TrackingData } from '../../domain/models/tracking/tracking_data';
import { TrackingEvent } from '../../domain/models/tracking/tracking_event';
import { TrackingWkt } from '../../domain/models/tracking/tracking_wkt';
import { StreetItemViewModel } from '../view-models/street-item.view-model';
import { DistributionStreet } from '../../domain/models/distribution/street';
import { DeviceViewModel } from '../view-models/device.view-model';
import { DeviceConfiguration } from '../view-models/device-configuration';
import { AddressItemViewModel } from '../view-models/address-item.view-model';
import { DistributionAddress } from '../../domain/models/distribution/distribution-address';
import { DistributionType } from '../../domain/enums/distribution-type';
import { AddressStatus } from '../../domain/enums/address-status';
import { PopupData } from '../../../../../libs/map/components/update-popup/popup-data';

const initialState: DistributionState = {
    detail: { entity: null, loading: false, error: false, addressItems: [], streetItems: [], filteredStreetItems: [] },
    distributions: { byId: {}, allIds: [], visibleIds: [], loading: false, error: false },
    sectors: { byId: {}, allIds: [], visibleIds: [], loading: false, error: false },
    mapExpanded: false,
    mapExtent: Constants.Extents.Belgium,
    mapFeatureThreshold: null,
    inSearchMode: false,
    trackingData: { byId: {}, allIds: [], loading: false, error: false },
    streets: { byId: {}, allIds: [], loading: false, error: false },
    addresses: { byId: {}, allIds: [], loading: false, error: false },
    selectedDevice: null,
    deviceConfigurations: [],
    selectedDistribution: null,
    showIcons: false,
    showDistribution: true,
    showEarlyLate: false,
    popupData: undefined,
    searchData: undefined,
    weeks: { byWeekNumber: {}, allWeekNumbers: [], activeWeekNumber: null, loading: false }
};

export function getDistributionBySectorId(distributions: any, sectorId: string): DistributionListItemViewModel {
    const distItems: DistributionListItemViewModel[] = Object.values(distributions.visibleIds)
        .map((id: string) => distributions.byId[id].sectorId === sectorId ? distributions.byId[id] : null)
        .filter((distribution: DistributionListItemViewModel) => !!distribution);
    return distItems[0];
}

export function getFirstDeviceFromDistribution(distribution: DistributionViewModel): string {
    return distribution.devices && distribution.devices[0] ? distribution.devices[0].id : null;
}

export function getNormalizedDistributionState(distributions: DistributionListItemViewModel[]): { [key: string]: DistributionListItemViewModel } {
    const distributionById: { [key: string]: DistributionListItemViewModel } = {};
    distributions.forEach((distribution: DistributionListItemViewModel) => {
        distributionById[distribution.distributionId] = distribution;
    });
    return distributionById;
}

export function getNormalizedSectorState(sectors: SectorPolygon[]): { [key: string]: SectorPolygon } {
    const sectorById: { [key: string]: SectorPolygon } = {};
    sectors.forEach((sector: SectorPolygon) => {
        sectorById[sector.id] = sector;
    });
    return sectorById;
}

export function getNormalizedStreets(streets: Street[]): { [key: string]: Street } {
    const streetById: { [key: string]: Street } = {};
    streets.forEach((street: Street) => {
        streetById[street.streetId] = street;
    });
    return streetById;
}
export function getNormalizedAddresses(addresses: Address[]): { [key: string]: Address } {
    const addressById: { [key: string]: Address } = {};
    addresses.forEach((address: Address) => {
        addressById[address.addressId] = address;
    });
    return addressById;
}
export function getDistributionAddresses(distributionAddresses: DistributionAddress[], addresses: Address[]): AddressItemViewModel[] {
    const addressItems: AddressItemViewModel[] = [];
    distributionAddresses.forEach((distributionAddress: DistributionAddress) => {
        let addressInfo: Address = addresses.find((s: Address) => s.addressId === distributionAddress.addressId);
        let addressItem: AddressItemViewModel = new AddressItemViewModel({
            addressId: distributionAddress.addressId,
            x: addressInfo.x,
            y: addressInfo.y,
            subAddresses: addressInfo.subAddresses,
            streetName: addressInfo.streetName,
            houseNumber: addressInfo.houseNumber,
            postalCode: addressInfo.postalCode,
            city: addressInfo.city,
            distributionType: distributionAddress.distributionType,
            modifiedBy: addressInfo.modifiedBy,
            modifiedAt: addressInfo.modifiedAt,
            visited: distributionAddress.visited
        });
        addressItems.push(addressItem);
    });
    return addressItems;
}
export function getDistributionStreets(distributionStreets: DistributionStreet[], streets: Street[]): StreetItemViewModel[] {
    const streetItems: StreetItemViewModel[] = [];
    streets.forEach((street: Street) => {
        let distributionStreet: DistributionStreet = distributionStreets.find((s: DistributionStreet) => s.streetId === street.streetId);
        let streetItem: StreetItemViewModel = new StreetItemViewModel({
            id: street.streetId,
            streetId: street.streetId,
            streetName: street.streetName,
            houseNumbers: street.houseNumbers,
            polygon: street.polygon,
            percentageComplete: distributionStreet ? distributionStreet.percentageComplete : 0,
            percentageCompleteEarly: distributionStreet ? distributionStreet.percentageCompleteEarly : 0,
            percentageCompleteLate: distributionStreet ? distributionStreet.percentageCompleteLate : 0
        });

        if (distributionStreet === undefined) {
            streetItem.distributionTiming = DistributionTiming.None;
            streetItems.push(streetItem);
        } else if (distributionStreet.percentageComplete !== 0) {
            streetItem.distributionTiming = DistributionTiming.OnTime;
            streetItems.push(streetItem);
        } else {
            if (distributionStreet.distributionTiming === DistributionTiming.Both) {
                streetItem.distributionTiming = DistributionTiming.Early;
                streetItems.push(streetItem);
                let extraItem: StreetItemViewModel = new StreetItemViewModel({
                    id: street.streetId,
                    streetId: street.streetId,
                    streetName: street.streetName,
                    houseNumbers: street.houseNumbers,
                    polygon: street.polygon,
                    percentageComplete: distributionStreet.percentageComplete,
                    percentageCompleteEarly: distributionStreet.percentageCompleteEarly,
                    percentageCompleteLate: distributionStreet.percentageCompleteLate,
                    distributionTiming: DistributionTiming.Late
                });
                streetItems.push(extraItem);
            } else if (distributionStreet.distributionTiming === DistributionTiming.Early) {
                streetItem.distributionTiming = DistributionTiming.Early;
                streetItems.push(streetItem);
            } else if (distributionStreet.distributionTiming === DistributionTiming.Late) {
                streetItem.distributionTiming = DistributionTiming.Late;
                streetItems.push(streetItem);
            } else {
                streetItem.distributionTiming = DistributionTiming.OnTime;
                streetItems.push(streetItem);
            }
        }
    });
    return streetItems;
}

export function getNormalizedTrackingDataState(trackingData: TrackingData): { [key: string]: DeviceTrackingData } {
    const trackingByDeviceId: { [key: string]: DeviceTrackingData } = {};
    if (trackingData === undefined) return {};
    trackingData.deviceTrackingData.forEach((tracking: DeviceTrackingData) => {
        trackingByDeviceId[tracking.deviceId] = tracking;
    });
    return trackingByDeviceId;
}

export function getNormalizedWeekState(weeks: WeekOfYear[]): { [key: string]: WeekOfYear } {
    const weekByNumber: { [key: string]: WeekOfYear } = {};
    weeks.forEach((week: WeekOfYear) => {
        weekByNumber[week.weekNumber] = week;
    });
    return weekByNumber;
}

export function getVisibleSectorIds(distributions: { [key: string]: DistributionListItemViewModel }, visibleIds: string[]): string[] {
    return visibleIds.map((id: string) => distributions[id].sectorId);
}

export function hasWktDefined(sectorPolygon: SectorPolygon): boolean {
    return sectorPolygon.wkt.length > 0;
}

export function prepareDeviceConfiguration(devices: DeviceViewModel[]): DeviceConfiguration[] {
    let configurations: DeviceConfiguration[] = [];
    if (devices && devices.length > 0) {
        devices.forEach((device: DeviceViewModel) => {
            configurations.push({
                deviceId: device.id,
                toggled: false,
                deviceColor: device.deviceColor
            });
        });
    }
    return configurations;
}
export function preparePopupData(addressId: string, state: DistributionState): PopupData {
    const address: Address = state.addresses.byId[addressId];
    let data: PopupData = new PopupData({
        addressId: address.addressId,
        street: address.streetName,
        houseNumber: address.houseNumber,
        city: address.city,
        postalCode: address.postalCode,
        weight: address.subAddresses,
        type: Constants.DistributionTypeLabel.get(address.distributionType),
        lastModified: address.modifiedAt,
        lastModifiedBy: address.modifiedBy,
    });
    return data;
}

export function distributionsReducer(state: DistributionState = initialState, action: AllActions.All): DistributionState {
    switch (action.type) {
        case AllActions.FILTER_DISTRIBUTIONS:
            return {
                ...state,
                distributions: {
                    ...state.distributions,
                    visibleIds: action.payload
                },
                sectors: {
                    ...state.sectors,
                    visibleIds: getVisibleSectorIds(state.distributions.byId, action.payload)
                },
                selectedDistribution: null
            };
        case AllActions.LOAD_DISTRIBUTIONS:
            return {
                ...state,
                detail: {
                    ...state.detail,
                    entity: null
                },
                distributions: {
                    ...state.distributions,
                    loading: true,
                    error: false
                },
                sectors: {
                    ...state.sectors,
                    byId: {},
                    allIds: [],
                    visibleIds: [],
                    loading: false,
                    error: false
                },
                streets: {
                    ...state.streets,
                    byId: {},
                    allIds: [],
                    loading: false,
                    error: false
                },
                addresses: {
                    ...state.addresses,
                    byId: {},
                    allIds: [],
                    loading: false,
                    error: false
                },
            };
        case AllActions.LOAD_DISTRIBUTIONS_SUCCESS:
            const normDists: { [key: string]: DistributionListItemViewModel } = getNormalizedDistributionState(action.payload);
            const normDistIds: string[] = Object.keys(normDists);
            return {
                ...state,
                distributions: {
                    ...state.distributions,
                    byId: normDists,
                    allIds: normDistIds,
                    visibleIds: normDistIds,
                    loading: false
                }
            };
        case AllActions.LOAD_DISTRIBUTIONS_FAILED:
            return {
                ...state,
                distributions: {
                    ...state.distributions,
                    byId: {},
                    allIds: [],
                    visibleIds: [],
                    loading: false,
                    error: true
                }
            };
        case AllActions.LOAD_DISTRIBUTION_DETAIL:
            return {
                ...state,
                sectors: {
                    ...state.sectors,
                    byId: {},
                    allIds: [],
                    visibleIds: []
                },
                detail: {
                    ...state.detail,
                    loading: true,
                    error: false
                },
                showEarlyLate: true,
            };
        case AllActions.LOAD_DISTRIBUTION_DETAIL_SUCCESS:
            return {
                ...state,
                detail: {
                    ...state.detail,
                    entity: action.payload,
                    loading: false
                },
                deviceConfigurations: prepareDeviceConfiguration(action.payload.devices),
                selectedDevice: getFirstDeviceFromDistribution(action.payload)
            };
        case AllActions.LOAD_DISTRIBUTION_DETAIL_FAILED:
            return {
                ...state,
                detail: {
                    ...state.detail,
                    entity: null,
                    loading: false,
                    error: true
                }
            };
        case AllActions.LOAD_TRACKING_DATA:
            return {
                ...state,
                trackingData: {
                    ...state.trackingData,
                    loading: true,
                    error: false
                }
            };
        case AllActions.LOAD_TRACKING_DATA_SUCCESS:
            const normTrackingData: { [key: string]: DeviceTrackingData } = getNormalizedTrackingDataState(action.payload);
            const normDeviceIds: string[] = Object.keys(normTrackingData);
            return {
                ...state,
                trackingData: {
                    ...state.trackingData,
                    byId: normTrackingData,
                    allIds: normDeviceIds,
                    loading: false
                }
            };
        case AllActions.LOAD_TRACKING_DATA_FAILED:
            return {
                ...state,
                trackingData: {
                    ...state.trackingData,
                    byId: {},
                    allIds: [],
                    loading: false,
                    error: true
                }
            };
        case AllActions.SET_FEATURE_THRESHOLD:
            return {
                ...state,
                mapFeatureThreshold: action.payload
            };
        case AllActions.SET_MAP_EXPANSION:
            return {
                ...state,
                mapExpanded: action.payload
            };
        case AllActions.TOGGLE_MAP:
            return {
                ...state,
                mapExpanded: !state.mapExpanded
            };
        case AllActions.LOAD_SECTORS:
            return {
                ...state,
                sectors: {
                    ...state.sectors,
                    loading: true,
                    error: false
                }
            };
        case AllActions.LOAD_SECTORS_SUCCESS:
            const normSectors: { [key: string]: SectorPolygon } = getNormalizedSectorState(action.payload);
            const normSectorIds: string[] = Object.keys(normSectors);
            return {
                ...state,
                sectors: {
                    ...state.sectors,
                    byId: normSectors,
                    allIds: normSectorIds,
                    visibleIds: normSectorIds,
                    loading: false
                }
            };
        case AllActions.LOAD_SECTORS_FAILED:
            return {
                ...state,
                sectors: {
                    ...state.sectors,
                    byId: {},
                    allIds: [],
                    visibleIds: [],
                    loading: false,
                    error: true
                }
            };
        case AllActions.LOAD_STREETS:
            return {
                ...state,
                streets: {
                    ...state.streets,
                    loading: true,
                    error: false
                }
            };
        case AllActions.LOAD_STREETS_SUCCESS:
            const normStreets: { [key: string]: Street } = getNormalizedStreets(action.streets);
            return {
                ...state,
                detail: {
                    ...state.detail,
                    streetItems: getDistributionStreets(action.distributionStreets, action.streets),
                    filteredStreetItems: getDistributionStreets(action.distributionStreets, action.streets),
                },
                streets: {
                    ...state.streets,
                    byId: normStreets,
                    allIds: Object.keys(normStreets),
                    loading: false
                }
            };
        case AllActions.LOAD_STREETS_FAILED:
            return {
                ...state,
                streets: {
                    ...state.streets,
                    byId: {},
                    allIds: [],
                    loading: false,
                    error: true
                }
            };
        case AllActions.FILTER_STREETS_ON_DISTRIBUTION:
            return {
                ...state,
                detail: {
                    ...state.detail,
                    filteredStreetItems: action.payload != null ? state.detail.streetItems.filter((street: StreetItemViewModel) => street.distributionTiming === action.payload) : state.detail.streetItems
                }
            };
        case AllActions.LOAD_ADDRESSES:
            return {
                ...state,
                addresses: {
                    ...state.addresses,
                    loading: true,
                    error: false
                }
            };
        case AllActions.LOAD_ADDRESSES_SUCCESS:
            const normAddresses: { [key: string]: Address } = getNormalizedAddresses(action.addresses);
            return {
                ...state,
                detail: {
                    ...state.detail,
                    addressItems: getDistributionAddresses(action.distributionAddresses, action.addresses),
                },
                addresses: {
                    ...state.addresses,
                    byId: normAddresses,
                    allIds: Object.keys(normAddresses),
                    loading: false
                }
            };
        case AllActions.LOAD_ADDRESSES_FAILED:
            return {
                ...state,
                addresses: {
                    ...state.addresses,
                    byId: {},
                    allIds: [],
                    loading: false,
                    error: true
                }
            };
        case AllActions.CLOSE_ERROR_DIALOG:
            return {
                ...state,
                detail: {
                    ...state.detail,
                    error: false
                },
                distributions: {
                    ...state.distributions,
                    error: false
                },
                sectors: {
                    ...state.sectors,
                    error: false
                },
            };
        case AllActions.SELECT_DEVICE:
            return {
                ...state,
                selectedDevice: action.payload
            };
        case AllActions.SELECT_DISTRIBUTION:
            return {
                ...state,
                selectedDistribution: action.payload
            };
        case AllActions.LOAD_WEEKS:
            return {
                ...state,
                weeks: {
                    ...state.weeks,
                    loading: true
                }
            };
        case AllActions.LOAD_WEEKS_COMPLETED:
            const normWeeks: { [key: string]: WeekOfYear } = getNormalizedWeekState(action.payload);
            const normWeekNumbers: number[] = Object.keys(normWeeks).map((weekNumber: string) => parseInt(weekNumber, 10));
            return {
                ...state,
                weeks: {
                    ...state.weeks,
                    byWeekNumber: normWeeks,
                    allWeekNumbers: normWeekNumbers,
                    loading: false
                }
            };
        case AllActions.SET_ACTIVE_WEEK:
            return {
                ...state,
                weeks: {
                    ...state.weeks,
                    activeWeekNumber: action.payload
                }
            };
        case AllActions.SHOW_EARLY_LATE:
            return {
                ...state,
                showEarlyLate: action.payload
            };
        case AllActions.TOGGLE_SHOW_ALL_DEVICES:
            return {
                ...state,
                deviceConfigurations: state.deviceConfigurations.map((d: DeviceConfiguration) => {
                    d.toggled = action.payload;
                    return d;
                }),
            };
        case AllActions.TOGGLE_SHOW_ICONS:
            return {
                ...state,
                showIcons: action.payload
            };
        case AllActions.TOGGLE_SHOW_DISTRIBUTION:
            return {
                ...state,
                showDistribution: action.payload
            };
        case AllActions.TOGGLE_DEVICE_CHANGED:
            return {
                ...state,
                deviceConfigurations: state.deviceConfigurations.map((d: DeviceConfiguration) => {
                    if (d.deviceId === action.payload.id) {
                        d.toggled = action.payload.value;
                    }
                    return d;
                }),
            };
        case AllActions.SET_IN_SEARCH_MODE:
            return {
                ...state,
                inSearchMode: action.payload,
                searchData: undefined
            };
        case AllActions.SHOW_UPDATE_POPUP:
            return {
                ...state,
                searchData: undefined,
                inSearchMode: false,
                popupData: preparePopupData(action.payload, state)
            };
        case AllActions.CLOSE_POPUP:
            return {
                ...state,
                popupData: undefined,
            };
        case AllActions.SET_SEARCH_DATA:
            return {
                ...state,
                searchData: action.payload
            };
        default:
            return state;
    }
}

export const selectDistributionState: MemoizedSelector<object, DistributionState> = createFeatureSelector<DistributionState>('distributions');

// ---- DISTRIBUTIONS
export const getDistributions: MemoizedSelector<object, DistributionListItemViewModel[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        if (state.distributions) {
            return Object.values(state.distributions.byId);
        } else {
            return [];
        }
    });

export const getDistributionDetail: MemoizedSelector<object, DistributionViewModel> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.detail.entity
);

export const getSelectedDevice: MemoizedSelector<object, string> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.selectedDevice
);

// ---- SECTORS
export const getFilteredSectorIds: MemoizedSelector<object, string[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.distributions.visibleIds.map((id: string) => state.distributions.byId[id].sectorId)
);

export const getSectorFeatures: MemoizedSelector<object, any> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        if (!state.sectors) {
            return [];
        }
        return state.sectors.allIds
            .filter((id: string) => {
                if (state.sectors.visibleIds.length > state.mapFeatureThreshold) {
                    return false;
                }
                return hasWktDefined(state.sectors.byId[id]) && state.sectors.visibleIds.indexOf(id) > -1;
            })
            .map((id: string) => {
                let features: any = [];
                let counter: number = 0;
                const feature: any = FormatUtils.readWkt(state.sectors.byId[id].wkt, Constants.Projections.Data, Constants.Projections.Map);
                feature.setId(id + '_' + counter);
                feature.setProperties({ transparency: '0.5', strokeTransparency: '1' });
                if (state.detail && state.detail.entity) {
                    feature.setProperties({ transparency: '0.2', strokeTransparency: '0', progress: state.detail.entity.distributionProgress });
                } else {
                    let distribution: DistributionListItemViewModel = null;
                    if (state.selectedDistribution && state.selectedDistribution.sectorId === id) {
                        distribution = state.distributions.byId[state.selectedDistribution.distributionId];
                    } else {
                        distribution = getDistributionBySectorId(state.distributions, id);
                    }
                    feature.setProperties({ transparency: '0.5', strokeTransparency: '1', progress: distribution ? distribution.distributionProgress + distribution.distributionProgressEarly + distribution.distributionProgressLate : undefined });
                }

                features.push(feature);
                return features;
            });
    });

// ---- MAP
export const getMapExpanded: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.mapExpanded
);

export const getMapSize: MemoizedSelector<object, string> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        let mapSize: MapSize = null;
        if (state.detail.entity) {
            mapSize = state.mapExpanded ? MapSize.Full : MapSize.Half;
        } else {
            mapSize = state.mapExpanded ? MapSize.Half : MapSize.Small;
        }
        return Constants.MapClass.get(mapSize);
    }
);

export const getMapExtent: MemoizedSelector<object, number[]> = createSelector(
    selectDistributionState,
    getSectorFeatures,
    (state: DistributionState, features: any) => {
        let flattenedFeatures: any = _.flatten(features);
        if (state.selectedDistribution) {
            const sectorId: string = state.distributions.byId[state.selectedDistribution.distributionId].sectorId;
            let featuresArray: any = [];
            const featuresFiltered: any = flattenedFeatures.find((ftr: any) => ftr.getId().toString().contains(sectorId));
            featuresFiltered.forEach((element: any) => {
                featuresArray.push(element.getGeometry().getExtent());
            });
            return featuresArray.length > 0 ? featuresArray : null;
        } else if (!flattenedFeatures.length) {
            return state.mapExtent;
        } else {
            return MapUtils.getExtentFromFeatures(flattenedFeatures);
        }
    }
);

// ---- WEEKS
export const getActiveWeek: MemoizedSelector<object, WeekOfYear> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.weeks.byWeekNumber[state.weeks.activeWeekNumber]
);

export const getWeeks: MemoizedSelector<object, WeekOfYear[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        if (state.weeks) {
            return Object.values(state.weeks.byWeekNumber)
                .sort((weekA: WeekOfYear, weekB: WeekOfYear) => (weekB.weekNumber) - (weekA.weekNumber));
        } else {
            return [];
        }
    });

// ---- UI
export const getLoading: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.distributions.loading || state.sectors.loading || state.streets.loading || state.addresses.loading || state.weeks.loading || state.detail.loading
);

export const getLoadingError: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.distributions.error || state.sectors.error || state.addresses.error || state.streets.error || state.detail.error
);

export const tooManyFeatures: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.sectors.visibleIds.length > state.mapFeatureThreshold
);

export const getDeviceFeatures: MemoizedSelector<object, Feature[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        if (!state.trackingData || state.trackingData.allIds.length === 0) {
            return;
        }
        let features: Feature[] = [];
        let addedEvents: TrackingEvent[] = [];
        state.trackingData.allIds.forEach((deviceId: string) => {
            let deviceConfiguration: DeviceConfiguration = state.deviceConfigurations.find((d: DeviceConfiguration) => d.deviceId === deviceId);
            if (deviceConfiguration === undefined || !deviceConfiguration.toggled) { return; }
            if (state.showIcons) {
                state.trackingData.byId[deviceId].trackingEvents.sort((e: TrackingEvent) => e.trackingDataType).forEach((trackingEvent: TrackingEvent) => {
                    let duplicateEvent: TrackingEvent = addedEvents.find((e: TrackingEvent) => e.x === trackingEvent.x && e.y === trackingEvent.y);
                    if (duplicateEvent === undefined || (duplicateEvent !== undefined && duplicateEvent.trackingDataType > trackingEvent.trackingDataType)) {
                        addedEvents.push(trackingEvent);
                    }
                });
            }
            state.trackingData.byId[deviceId].trackingWkts.forEach((trackingWkt: TrackingWkt) => {
                let projectionLevel: ProjectionLevel = ProjectionLevel.Overview;
                switch (trackingWkt.skipLevel) {
                    case 1: projectionLevel = ProjectionLevel.TrackingOverview; break;
                    case 5: projectionLevel = ProjectionLevel.Precise; break;
                    case 10: projectionLevel = ProjectionLevel.Overview; break;
                    default: projectionLevel = ProjectionLevel.Overview; break;
                }
                let pointCounter: number = 1;
                trackingWkt.wkts.forEach((wkt: string) => {
                    let feature: Feature = FormatUtils.readWkt(wkt, Constants.Projections.Data, Constants.Projections.Map);
                    feature.setProperties({ deviceId: deviceId, type: EventType.TrackingString, projectionLevel: projectionLevel, color: deviceConfiguration.deviceColor });
                    features.push(feature);

                    if (trackingWkt.skipLevel === 1 && state.showIcons) {
                        let coordinateString: string = wkt.substring(12, wkt.length - 1);
                        let coords: string[] = coordinateString.split(',');
                        let pointerString = pointCounter.toString();
                        let coordCount: number = pointCounter === 1 ? 1 : parseInt(pointerString.charAt(pointerString.length - 1));
                        coords.forEach((coordinate: string, pointIndex: number) => {
                            let coordinates: string[] = coordinate.trim().split(' ');
                            let duplicateEvent: TrackingEvent = addedEvents.find((e: TrackingEvent) => e.x === parseFloat(coordinates[0]) && e.y === parseFloat(coordinates[1]));
                            if (duplicateEvent !== undefined) {
                                coordCount++;
                                pointCounter++;
                            } else {
                                let pointfeature: Feature = new Feature({
                                    geometry: new Point(transform([coordinates[0], coordinates[1]], Constants.Projections.Data, Constants.Projections.Map))
                                });
                                let trackingLevel: number = ProjectionLevel.TrackingDetailed;
                                if (pointIndex === 0) {
                                    trackingLevel = ProjectionLevel.TrackingOverview;
                                } else if ((coordCount % 10) === 0) {
                                    trackingLevel = ProjectionLevel.TrackingOverview;
                                    coordCount = 0;
                                } else if ((coordCount % 5) === 0) {
                                    trackingLevel = ProjectionLevel.TrackingPrecise;
                                }
                                coordCount++;
                                pointfeature.setProperties({ deviceId: deviceId, type: EventType.TrackingPoint, projectionLevel: trackingLevel, number: pointCounter, color: deviceConfiguration.deviceColor });
                                pointCounter++;
                                features.push(pointfeature);
                            }
                        });
                    }
                });
            });
        });
        return features;
    }
);
export const getAddressFeatures: MemoizedSelector<object, Feature[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        let features: Feature[] = [];
        if (!state.detail.addressItems || state.detail.addressItems.length === 0) {
            return;
        }
        state.detail.addressItems.forEach((addressItem: AddressItemViewModel) => {
            const feature: Feature = new Feature({
                geometry: new Point(transform([addressItem.x, addressItem.y], Constants.Projections.Data, Constants.Projections.Map))
            });
            feature.setId(addressItem.addressId);
            let addressColor: string;
            switch (addressItem.distributionType) {
                case DistributionType.ToDistribute:
                    addressColor = addressItem.visited ? Constants.AddressColor.get(AddressStatus.Visited) : Constants.AddressColor.get(AddressStatus.UnVisited);
                    break;
                case DistributionType.NDZ:
                    addressColor = Constants.AddressColor.get(AddressStatus.NDZ);
                    break;
                case DistributionType.Sticker:
                    addressColor = Constants.AddressColor.get(AddressStatus.Sticker);
                    break;
                case DistributionType.OtherPk:
                    addressColor = Constants.AddressColor.get(AddressStatus.OtherPk);
                    break;
                case DistributionType.FalseAddressPoint:
                    addressColor = Constants.AddressColor.get(AddressStatus.FalseAddressPoint);
                    break;
                default:
                    addressColor = Constants.AddressColor.get(AddressStatus.NDZ);
                    break;
            }
            feature.setProperties({
                addressId: addressItem.addressId,
                street: addressItem.streetName + ' ' + addressItem.houseNumber,
                city: addressItem.postalCode + ' ' + addressItem.city,
                weight: addressItem.subAddresses,
                type: addressItem.distributionType,
                lastModified: addressItem.modifiedAt === undefined ? '' : addressItem.modifiedAt.toDateString(),
                lastModifiedBy: addressItem.modifiedBy === undefined ? '' : addressItem.modifiedBy,
                addressColor: addressColor
            });
            features.push(feature);
        });
        return features;
    }
);
export const getStreetFeatures: MemoizedSelector<object, Feature[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        let features: Feature[] = [];
        if (!state.detail.streetItems || state.detail.streetItems.length === 0 || !state.showDistribution) {
            return [];
        }
        state.detail.streetItems.forEach((streetItem: StreetItemViewModel) => {
            if (streetItem.polygon !== undefined) {
                const feature: any = FormatUtils.readWkt(streetItem.polygon, Constants.Projections.Data, Constants.Projections.Map);
                feature.setId(streetItem.id);
                feature.setProperties({ transparency: '0.4', strokeTransparency: '0.4', isNDS: streetItem.distributionTiming === DistributionTiming.None });
                if (state.showEarlyLate) {
                    feature.setProperties({ progress: streetItem.getAllProgress() });
                } else {
                    feature.setProperties({ progress: streetItem.percentageComplete });
                }
                features.push(feature);
            }
        });
        return features;
    }
);
export const getEventFeatures: MemoizedSelector<object, Feature[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        if (!state.trackingData || state.trackingData.allIds.length === 0) {
            return [];
        }
        let features: Feature[] = [];
        let addedEvents: TrackingEvent[] = [];
        state.trackingData.allIds.forEach((deviceId: string) => {
            let deviceConfiguration: DeviceConfiguration = state.deviceConfigurations.find((d: DeviceConfiguration) => d.deviceId === deviceId);
            if (deviceConfiguration === undefined || !deviceConfiguration.toggled) { return; }
            if (state.showIcons) {
                state.trackingData.byId[deviceId].trackingEvents.sort((e: TrackingEvent) => e.trackingDataType).forEach((trackingEvent: TrackingEvent) => {
                    let duplicateEvent: TrackingEvent = addedEvents.find((e: TrackingEvent) => e.x === trackingEvent.x && e.y === trackingEvent.y);
                    if (duplicateEvent === undefined || (duplicateEvent !== undefined && duplicateEvent.trackingDataType > trackingEvent.trackingDataType)) {
                        let feature: Feature = new Feature({
                            geometry: new Point(transform([trackingEvent.x, trackingEvent.y], Constants.Projections.Data, Constants.Projections.Map))
                        });
                        feature.setProperties({ deviceId: deviceId, type: trackingEvent.trackingDataType, projectionLevel: ProjectionLevel.Events });
                        features.push(feature);
                        addedEvents.push(trackingEvent);
                    }
                });
            }
        });
        return features;
    }
);
export const getFilteredStreets: MemoizedSelector<object, StreetItemViewModel[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.detail.filteredStreetItems
);

export const getDeviceConfigurations: MemoizedSelector<object, DeviceConfiguration[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.deviceConfigurations
);
export const getShowAllDevices: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.deviceConfigurations.every((d: DeviceConfiguration) => d.toggled)
);
export const getShowIcons: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.showIcons
);
export const getShowDistribution: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.showDistribution
);
export const getInSearchMode: MemoizedSelector<object, boolean> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.inSearchMode
);
export const getPopupData: MemoizedSelector<object, PopupData> = createSelector(
    selectDistributionState,
    (state: DistributionState) => state.popupData
);
export const getFilteredAddresses: MemoizedSelector<object, Address[]> = createSelector(
    selectDistributionState,
    (state: DistributionState) => {
        if (!state.searchData || state.addresses.allIds.length === 0) {
            return [];
        }
        let fitleredAddresses: Address[] = [];
        state.addresses.allIds.forEach((id: string) => {
            const addres: Address = state.addresses.byId[id];
            if (state.searchData.street && !addres.streetName.toLowerCase().startsWith(state.searchData.street.toLowerCase())) {
                return;
            }
            if (state.searchData.number && !addres.houseNumber.toLowerCase().startsWith(state.searchData.number.toLowerCase())) {
                return;
            }
            if (state.searchData.city && !addres.city.toLowerCase().startsWith(state.searchData.city.toLowerCase())) {
                return;
            }
            if (state.searchData.postcode && !addres.postalCode.toLowerCase().startsWith(state.searchData.postcode.toLowerCase())) {
                return;
            }
            fitleredAddresses.push(addres);
        });
        return fitleredAddresses;
    }
);



// WEBPACK FOOTER //
// ./src/app/modules/distribution/store/distribution.reducer.ts